/*!
 * RebelsMarket Fit Finder — modal wizard (theme asset).
 * Self-injects markup + styles on first use. Opens on click of any [data-fit-finder-open].
 * Requires fit-recommender.js loaded first.
 *
 * Flow (measure): intro → measure feet (length + girth, per foot; girth required) →
 *                 fit problems → width confirm → result.
 * Flow (brands):  intro → pick brands → size + how it fits per brand →
 *                 fit problems → foot shape (width fallback) → result.
 * An optional email/phone capture step follows result when FIT_FINDER.captureContact is on.
 *
 * Config: window.FIT_FINDER = { submitEndpoint, brandName, introImage, itemType, productFormId, guideUrl }
 * Event:  dispatches `fitfinder:selected` on <document> when a size is applied.
 */
(function () {
  "use strict";
  if (window.__FIT_FINDER_INIT) return;
  window.__FIT_FINDER_INIT = true;
  var CFG = window.FIT_FINDER = window.FIT_FINDER || {};
  CFG.brandName = CFG.brandName || "Rebels Market";
  var STORE_KEY = "rm_fit_profile";

  /* ---------------- styles (warm-linen + copper-amber brand palette) --------------- */
  var STYLE =
  ".ff-o{--bg:#edeae3;--surface:#f5f2eb;--surface2:#e2ddd4;--input:#e8e3da;--track:#dedad2;" +
    "--ink:#1c1915;--muted:#7a7265;--disabled:#9a9488;--accent:#b07d3e;--success:#2a7a4f;" +
    "--btn:#1c1915;--btnText:#f5f2eb;--btnOff:#c5bfb5;--border:rgba(28,25,21,.12);--backdrop:rgba(28,25,21,.60);" +
    // z-index sits just above Shopify's cookie-consent banner (#shopify-pc__banner,
    // z-index:2000000), which is position:fixed and would otherwise float over the
    // lower half of this modal on mobile and eat taps (incl. the Next/Skip footer).
    "position:fixed;inset:0;background:var(--backdrop);display:none;align-items:center;justify-content:center;z-index:2000001;padding:16px;}" +
  ".ff-o.open{display:flex;}" +
  ".ff-m{background:var(--surface);color:var(--ink);width:100%;max-width:460px;max-height:92vh;border-radius:14px;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.35);font-family:inherit;}" +
  "@media(max-width:600px){.ff-o{padding:12px;}.ff-m{max-width:none;max-height:calc(100dvh - 24px);border-radius:14px;}}" +
  ".ff-h{padding:16px 18px 8px;}" +
  ".ff-h-top{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}" +
  ".ff-title{font-family:Georgia,'Times New Roman',serif;font-size:19px;font-weight:600;margin:0;letter-spacing:-.2px;}" +
  ".ff-x{background:none;border:0;font-size:24px;line-height:1;cursor:pointer;color:var(--ink);opacity:.7;}" +
  ".ff-step{font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);margin-top:10px;}" +
  ".ff-track{height:3px;background:var(--track);border-radius:2px;margin-top:8px;overflow:hidden;}" +
  ".ff-fill{height:100%;background:var(--accent);border-radius:2px;transition:width .25s;}" +
  ".ff-b{padding:6px 18px 16px;overflow:auto;flex:1;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;}" +
  ".ff-q{font-family:Georgia,'Times New Roman',serif;font-size:17px;margin:6px 0 4px;line-height:1.25;}" +
  ".ff-help{color:var(--muted);font-size:13px;margin:0 0 12px;line-height:1.5;}" +
  ".ff-f{padding:10px 18px 12px;border-top:1px solid var(--border);background:var(--surface);}" +
  ".ff-f-row{display:flex;align-items:center;justify-content:space-between;gap:10px;}" +
  ".ff-tag{text-align:center;font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:var(--muted);margin-top:10px;}" +
  ".ff-link{background:none;border:0;color:var(--muted);cursor:pointer;font-size:13px;letter-spacing:.08em;text-transform:uppercase;font-family:inherit;}" +
  ".ff-link:hover{color:var(--ink);}" +
  ".ff-primary{background:var(--btn);color:var(--btnText);border:0;border-radius:10px;padding:11px 22px;font-size:13px;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;font-family:inherit;}" +
  ".ff-primary:disabled{background:var(--btnOff);color:var(--surface);cursor:not-allowed;}" +
  ".ff-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(72px,1fr));gap:8px;}" +
  ".ff-tile{background:var(--input);border:1.5px solid transparent;border-radius:8px;padding:9px 6px;text-align:center;cursor:pointer;font-size:14px;color:var(--ink);font-family:inherit;transition:.12s;}" +
  ".ff-tile:hover{border-color:var(--accent);}" +
  ".ff-tile.sel{border-color:var(--ink);background:var(--surface);font-weight:600;}" +
  ".ff-tile.off{opacity:.4;cursor:not-allowed;}.ff-tile.off:hover{border-color:transparent;}" +
  ".ff-seg{display:grid;grid-auto-flow:column;grid-auto-columns:1fr;gap:8px;margin-bottom:16px;}" +
  ".ff-seg .ff-tile{padding:10px 8px;}" +
  ".ff-gtitle{font-size:13px;font-weight:700;letter-spacing:.04em;margin:20px 0 8px;}" +
  ".ff-gtitle .n{color:var(--muted);font-weight:400;}" +
  ".ff-chips{display:grid;grid-template-columns:repeat(auto-fill,minmax(120px,1fr));gap:8px;}" +
  ".ff-chips .ff-tile{padding:9px 8px;font-size:13px;}" +
  ".ff-problems{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:8px;}" +
  ".ff-problems .ff-tile{padding:12px 10px;font-size:14px;white-space:normal;line-height:1.3;}" +
  ".ff-shapes{display:grid;grid-template-columns:repeat(auto-fit,minmax(96px,1fr));gap:10px;}" +
  ".ff-shape{position:relative;background:var(--surface);border:1.5px solid var(--border);border-radius:12px;padding:14px 8px 12px;text-align:center;cursor:pointer;transition:.12s;}" +
  ".ff-shape:hover{border-color:var(--accent);}" +
  ".ff-shape.sel{border-color:var(--accent);background:rgba(176,125,62,.08);box-shadow:0 0 0 3px rgba(176,125,62,.15);}" +
  ".ff-shape svg{display:block;margin:0 auto 8px;}" +
  ".ff-shape .st{font-size:13px;font-weight:600;}.ff-shape .ss{font-size:12px;color:var(--muted);margin-top:1px;}" +
  ".ff-pop{position:absolute;top:-10px;left:50%;transform:translateX(-50%);background:var(--ink);color:var(--surface);font-size:10px;letter-spacing:.1em;text-transform:uppercase;padding:3px 9px;border-radius:20px;white-space:nowrap;}" +
  ".ff-feel{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-top:6px;}" +
  ".ff-lbl{font-size:13px;font-weight:600;margin:18px 0 8px;display:block;}" +
  ".ff-mini{font-size:12px;color:var(--muted);margin-top:8px;}" +
  ".ff-welcome{background:var(--bg);border-radius:12px;padding:16px;margin-bottom:18px;text-align:center;}" +
  ".ff-welcome .sz{font-family:Georgia,serif;font-size:30px;font-weight:700;}" +
  ".ff-o input[type=email],.ff-o input[type=tel],.ff-o input[type=number]{width:100%;padding:13px 14px;border:1px solid var(--border);border-radius:10px;font-size:15px;font-family:inherit;background:var(--input);color:var(--ink);}" +
  ".ff-badge{display:inline-block;font-size:11px;background:var(--accent);color:var(--surface);padding:2px 8px;border-radius:20px;margin-left:6px;vertical-align:middle;letter-spacing:.04em;}" +
  ".ff-method{display:block;width:100%;text-align:left;background:var(--surface);border:1.5px solid var(--border);border-radius:12px;padding:13px 14px;margin-bottom:8px;cursor:pointer;font-family:inherit;color:var(--ink);transition:.12s;}" +
  ".ff-method:hover{border-color:var(--accent);}.ff-method.sel{border-color:var(--accent);background:rgba(176,125,62,.08);}" +
  ".ff-method .t{font-weight:600;font-size:15px;display:block;}.ff-method .s{color:var(--muted);font-size:13px;margin-top:2px;display:block;line-height:1.35;}" +
  ".ff-mc-row{display:flex;align-items:center;gap:12px;}.ff-mc-tx{flex:1;min-width:0;}" +
  ".ff-mc-ic{width:34px;height:34px;border-radius:9px;background:var(--input);display:flex;align-items:center;justify-content:center;font-size:17px;flex:0 0 auto;}" +
  ".ff-badge2{font-size:11px;background:rgba(42,122,79,.12);color:var(--success);padding:3px 9px;border-radius:20px;white-space:nowrap;font-weight:600;flex:0 0 auto;}" +
  ".ff-check{display:flex;align-items:center;gap:12px;background:rgba(42,122,79,.08);border:1px solid rgba(42,122,79,.25);border-radius:12px;padding:14px;}" +
  ".ff-check .ck{width:36px;height:36px;border-radius:9px;background:var(--success);color:#fff;display:flex;align-items:center;justify-content:center;font-size:18px;flex:0 0 auto;}" +
  ".ff-check .t{font-weight:600;}.ff-check .s{color:var(--muted);font-size:13px;}" +
  ".ff-2col{display:grid;grid-template-columns:1fr 1fr;gap:10px;}" +
  ".ff-subcap{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);margin:0 0 4px;}" +
  ".ff-numlbl{font-size:12px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);margin:16px 0 6px;font-weight:600;}" +
  ".ff-inline{background:none;border:0;color:var(--accent);cursor:pointer;font-size:14px;font-family:inherit;padding:0;margin:2px 0 6px;}" +
  ".ff-mrow{display:flex;gap:12px;margin-bottom:18px;align-items:flex-start;}" +
  ".ff-thumb{width:72px;flex:0 0 auto;aspect-ratio:3/4;border-radius:8px;overflow:hidden;border:1px solid var(--border);background:var(--input);cursor:zoom-in;padding:0;position:relative;}" +
  ".ff-thumb img{width:100%;height:100%;object-fit:cover;display:block;}" +
  ".ff-thumb .zi{position:absolute;bottom:4px;right:4px;background:rgba(28,25,21,.6);color:#fff;font-size:11px;border-radius:5px;padding:1px 5px;line-height:1.4;}" +
  ".ff-fcol{flex:1;min-width:0;}" +
  ".ff-inwrap{position:relative;}.ff-unit{position:absolute;right:12px;top:50%;transform:translateY(-50%);color:var(--muted);font-size:13px;pointer-events:none;}" +
  ".ff-lb{position:fixed;inset:0;background:rgba(0,0,0,.82);z-index:2000002;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:28px;cursor:zoom-out;}" +
  ".ff-lb img{max-width:88%;max-height:66vh;object-fit:contain;border-radius:12px;background:#fff;}" +
  ".ff-lb .cap{color:#fff;text-align:center;margin-top:16px;max-width:440px;}.ff-lb .cap .t{font-weight:600;font-size:16px;}.ff-lb .cap .h{opacity:.85;font-size:13px;margin-top:6px;line-height:1.5;}" +
  ".ff-lb-x{position:absolute;top:14px;right:20px;color:#fff;font-size:30px;background:none;border:0;cursor:pointer;line-height:1;}" +
  ".ff-callout{background:rgba(176,125,62,.08);border:1px solid rgba(176,125,62,.2);border-radius:10px;padding:12px 14px;font-size:13px;margin:14px 0;}" +
  ".ff-guide{display:flex;align-items:center;gap:12px;background:rgba(176,125,62,.1);border:1.5px solid var(--accent);border-radius:12px;padding:14px 16px;margin:14px 0;text-decoration:none;color:var(--ink);transition:.12s;}" +
  ".ff-guide:hover{background:rgba(176,125,62,.18);}" +
  ".ff-guide-ic{font-size:22px;line-height:1;flex:none;}" +
  ".ff-guide-tx{display:flex;flex-direction:column;gap:2px;flex:1;min-width:0;}" +
  ".ff-guide-tx b{font-size:14px;}.ff-guide-tx span{font-size:12.5px;color:var(--muted);}" +
  ".ff-guide-arw{font-size:18px;color:var(--accent);flex:none;}" +
  ".ff-nobrand{border-top:1px solid var(--border);margin-top:22px;padding-top:18px;}" +
  ".ff-nobrand-msg{font-size:13.5px;color:var(--ink);margin-bottom:10px;line-height:1.5;}" +
  ".ff-nobrand-btn{background:none;border:1.5px solid var(--accent);color:var(--accent);border-radius:10px;padding:11px 18px;font-size:14px;font-family:inherit;cursor:pointer;font-weight:600;transition:.12s;}" +
  ".ff-nobrand-btn:hover{background:var(--accent);color:var(--surface);}" +
  ".ff-proof{display:flex;align-items:center;gap:14px;background:var(--ink);color:var(--surface);border-radius:12px;padding:16px 18px;margin:16px 0;}" +
  ".ff-proof .pct{font-family:Georgia,serif;font-size:34px;font-weight:700;line-height:1;flex:none;}" +
  ".ff-proof .pt{font-size:13px;line-height:1.45;}" +
  ".ff-consent{display:flex;gap:9px;margin-top:16px;font-size:13px;color:var(--ink);line-height:1.4;}" +
  ".ff-ts{margin-top:16px;min-height:65px;}" +
  ".ff-err{color:#9a2b1e;font-size:13px;margin-top:6px;display:none;}.ff-err.show{display:block;}" +
  ".ff-res{text-align:center;padding:6px 0;}" +
  ".ff-res .sz{font-family:Georgia,serif;font-size:44px;font-weight:700;letter-spacing:-1px;}" +
  ".ff-res .cv{color:var(--muted);font-size:14px;margin-top:4px;}" +
  ".ff-chip{display:inline-block;padding:5px 14px;border-radius:20px;font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;margin-top:14px;}" +
  ".ff-chip.high{background:rgba(42,122,79,.1);color:var(--success);}" +
  ".ff-chip.medium{background:rgba(176,125,62,.12);color:var(--accent);}" +
  ".ff-chip.low{background:rgba(154,43,30,.1);color:#9a2b1e;}" +
  ".ff-rec{background:var(--bg);border-radius:12px;padding:16px;margin-top:18px;font-size:14px;line-height:1.6;}" +
  ".ff-done{text-align:center;padding:24px 0;}.ff-done .big{font-size:44px;color:var(--success);}";

  var MARKUP =
    '<div class="ff-m">' +
      '<div class="ff-h"><div class="ff-h-top"><h2 class="ff-title">Find your size</h2>' +
        '<button class="ff-x" data-ff="x" aria-label="Close">×</button></div>' +
        '<div class="ff-step" data-ff="step"></div>' +
        '<div class="ff-track"><div class="ff-fill" data-ff="fill"></div></div></div>' +
      '<div class="ff-b" data-ff="body"></div>' +
      '<div class="ff-f">' +
        '<div class="ff-f-row"><button class="ff-link" data-ff="back">‹ Back</button>' +
          '<div style="display:flex;align-items:center;gap:16px;">' +
            '<button class="ff-link" data-ff="skip">Skip</button>' +
            '<button class="ff-primary" data-ff="next">Continue</button></div></div>' +
        '<div class="ff-tag">Made to Fit. Built to Last.</div>' +
      '</div>' +
    '</div>';

  /* ---------------- data ---------------- */
  var SNEAKER_BRANDS = ["nike", "adidas", "new_balance"];
  var DRESS_BRANDS = ["cole_haan", "johnston_murphy"];
  // Brannock reads a length-size and a width letter. These map cleanly onto our ladder
  // via the engine's width_letter_map (EE→2E, EEE→3E).
  var BRANNOCK_WIDTHS = [
    { v: "", label: "Not sure" }, { v: "B", label: "B · Narrow" }, { v: "D", label: "D · Standard" },
    { v: "EE", label: "EE · Wide" }, { v: "EEE", label: "EEE · Extra wide" }, { v: "EEEE", label: "EEEE · Widest" }
  ];
  var MAX_PER_GROUP = 3;
  var PROBLEMS = [
    { k: "cramped_length", label: "Too short / cramped" }, { k: "runs_long", label: "Too long" },
    { k: "tight_toe", label: "Tight across the toes" }, { k: "pinch_sides", label: "Pinches on the sides" },
    { k: "sloppy", label: "Too roomy / foot slides" }, { k: "heel_slip", label: "My heel slips" },
    { k: "high_instep", label: "Tight over the top (high instep)" }, { k: "bunions", label: "Bunions / wide forefoot" },
    { k: "none", label: "No problems" }
  ];
  var DRESS_WIDTHS = [
    { v: "", label: "Not sure" }, { v: "B", label: "B · Narrow" }, { v: "D", label: "D · Standard" },
    { v: "2E", label: "2E · Wide" }, { v: "3E", label: "3E · Extra wide" }, { v: "4E", label: "4E · Widest" }
  ];
  var UNIT_MM = { cm: 10, in: 25.4, mm: 1 };        // multiply a value in this unit to get mm
  var UNIT_NAME = { cm: "cm", in: "in", mm: "mm" };
  var UNIT_PH = {
    cm: { len: ["e.g. 26.7", "e.g. 27.0"], girth: ["e.g. 24.7", "e.g. 25.0"] },
    in: { len: ["e.g. 10.5", "e.g. 10.6"], girth: ["e.g. 9.7", "e.g. 9.8"] },
    mm: { len: ["e.g. 267", "e.g. 270"], girth: ["e.g. 247", "e.g. 250"] }
  };
  var SHAPES = [
    { key: "very_narrow", w: 0.74, t: "Very narrow", s: "A" }, { key: "narrow", w: 0.87, t: "Narrow", s: "B" },
    { key: "standard", w: 1.0, t: "Standard", s: "Medium", pop: true },     { key: "wide", w: 1.13, t: "Wide", s: "2E" },
    { key: "extra_wide", w: 1.20, t: "Extra wide", s: "3E" },
    { key: "widest", w: 1.27, t: "Widest", s: "4E" }
  ];
  var SHAPE_WIDTHS = {
    men: { very_narrow: "A", narrow: "B", standard: "D", wide: "2E", extra_wide: "3E", widest: "4E" },
    women: { very_narrow: "A", narrow: "A", standard: "B", wide: "D", extra_wide: "2E", widest: "4E" }
  };

  var F, o, body, stepEl, fillEl, back, next, skip;
  var state, result, i;

  function reset() {
    state = { gender: "men", region: "US", method: null, measurement: { unit: "cm", feet_match: "Same" }, brands: { sneaker: [], dress: [] }, entries: {}, brannock: { size: "", width: "" }, problems: [], shape: {}, contact: {}, scope: null };
    result = null; i = 0;
  }
  function selectedBrands() { return state.brands.sneaker.concat(state.brands.dress); }
  function feetDiffer() { return state.measurement.feet_match === "Different"; }
  // Girth is required in the measure flow — for one foot (Same) or both (Different).
  function measureValid() {
    var m = state.measurement;
    return feetDiffer()
      ? (+m.lenL > 0 && +m.girthL > 0 && +m.lenR > 0 && +m.girthR > 0)
      : (+m.len > 0 && +m.girth > 0);
  }
  function stepList() {
    var s = ["start"];
    if (state.method === "measure") {
      // Girth is required now, so width is always precise from the measurement — no
      // foot-shape fallback here. We still ask about fit problems (captured as notes).
      s.push("measure", "problems", "widthconfirm");
    } else if (state.method === "brands") {
      s.push("brands");
      selectedBrands().forEach(function (k) { s.push("brand:" + k); });
      s.push("problems", "shape");
    } else if (state.method === "brannock") {
      // Brannock gives size + width directly, so no foot-shape step is needed.
      s.push("brannock", "problems");
    }
    // Contact (email/phone) capture is off until the lead backend is live (ENG-79).
    return s.concat(CFG.captureContact ? ["result", "contact", "done"] : ["result"]);
  }
  function quizSteps() { return stepList().filter(function (s) { return ["start", "result", "contact", "done"].indexOf(s) < 0; }); }
  function curStep() { return stepList()[i]; }
  var OPTIONAL = { problems: 1, shape: 1 };

  function init() {
    if (!window.FitRecommender) { console.error("[FitFinder] fit-recommender.js not loaded"); return; }
    F = window.FitRecommender;
    var st = document.createElement("style"); st.textContent = STYLE; document.head.appendChild(st);
    o = document.createElement("div"); o.className = "ff-o"; o.setAttribute("role", "dialog"); o.setAttribute("aria-modal", "true");
    o.innerHTML = MARKUP; document.body.appendChild(o);
    body = g("body"); stepEl = g("step"); fillEl = g("fill"); back = g("back"); next = g("next"); skip = g("skip");
    g("x").onclick = close; back.onclick = goBack; next.onclick = goNext; skip.onclick = onSkip;
    o.addEventListener("click", function (e) { if (e.target === o) close(); });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape" && o.classList.contains("open")) close(); });
  }
  function g(n) { return o.querySelector('[data-ff="' + n + '"]'); }

  function open(opts) {
    if (!o) init(); if (!o) return;
    reset();
    if (opts && opts.gender) state.gender = opts.gender;
    if (opts && opts.scope) state.scope = opts.scope;
    o.classList.add("open"); render();
    // Lock now, again next frame, and once more after any drawer close animation —
    // the chart drawer we may have opened from also toggles body scroll lock.
    lockScroll(); requestAnimationFrame(lockScroll); setTimeout(lockScroll, 400);
  }
  function close() { if (o) { o.classList.remove("open"); unlockScroll(); } }

  // Lock the page behind the modal. position:fixed on <body> is the only approach that
  // reliably stops scroll bleed on iOS Safari; we restore the scroll position on close.
  // lockScroll is idempotent (re-applies styles) so it survives the drawer's own
  // scroll-lock teardown; the real scroll offset is read even if <body> is already fixed.
  var _lockY = 0, _locked = false;
  function _scrollY() {
    var b = document.body;
    if (b.style.position === "fixed" && b.style.top) return (-parseInt(b.style.top, 10)) || 0;
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }
  function lockScroll() {
    if (!o || !o.classList.contains("open")) return;
    if (!_locked) { _lockY = _scrollY(); _locked = true; }
    var b = document.body;
    b.style.position = "fixed"; b.style.top = (-_lockY) + "px";
    b.style.left = "0"; b.style.right = "0"; b.style.width = "100%";
  }
  function unlockScroll() {
    if (!_locked) return; _locked = false;
    var b = document.body;
    b.style.position = ""; b.style.top = ""; b.style.left = ""; b.style.right = ""; b.style.width = "";
    window.scrollTo(0, _lockY);
  }

  /* ---------------- helpers ---------------- */
  function footSVG(w) {
    return '<svg viewBox="0 0 100 130" width="52" height="68" aria-hidden="true">' +
      '<g transform="translate(50,0) scale(' + w + ',1) translate(-50,0)" fill="none" stroke="#7a7265" stroke-width="3" stroke-linejoin="round">' +
      '<path d="M50 10 C30 10 22 34 22 66 C22 100 30 122 50 122 C70 122 78 100 78 66 C78 34 70 10 50 10 Z"/>' +
      '<path d="M34 40 C40 32 60 32 66 40"/><path d="M42 52 h16 M42 60 h16 M42 68 h16"/></g></svg>';
  }
  function sizeValues(region, gender) {
    var w = gender === "women", a = [], s;                                          // men US 5.5–16, women US 7–16 (Brannock: women's = men's + 1, on a men's last); women's UK = US − 2
    if (region === "EU") { for (s = 36; s <= 50; s++) a.push(s); }
    else if (region === "UK") { for (s = (w ? 5 : 4.5); s <= (w ? 14 : 15); s += 0.5) a.push(s); }
    else { for (s = (w ? 7 : 5.5); s <= 16; s += 0.5) a.push(s); }
    return a;
  }
  function tileGrid(values, suffix, selected, onPick) {
    var wrap = document.createElement("div"); wrap.className = "ff-grid";
    values.forEach(function (v) {
      var b = btnTile(v + suffix, String(selected) === String(v)); b.onclick = function () { onPick(v); }; wrap.appendChild(b);
    });
    return wrap;
  }
  function q(t, h) { var a = document.createElement("div"); a.className = "ff-q"; a.textContent = t; body.appendChild(a);
    if (h) { var p = document.createElement("p"); p.className = "ff-help"; p.textContent = h; body.appendChild(p); } }
  function label(t) { var l = document.createElement("label"); l.className = "ff-lbl"; l.textContent = t; body.appendChild(l); }
  function btnTile(text, sel, off) { var b = document.createElement("button"); b.className = "ff-tile" + (sel ? " sel" : "") + (off ? " off" : ""); b.textContent = text; return b; }
  function problemLabel(k) { for (var n = 0; n < PROBLEMS.length; n++) if (PROBLEMS[n].k === k) return PROBLEMS[n].label.toLowerCase(); return k; }
  function loadProfile() { try { return JSON.parse(localStorage.getItem(STORE_KEY) || "null"); } catch (e) { return null; } }
  function saveProfile(p) { try { localStorage.setItem(STORE_KEY, JSON.stringify(p)); } catch (e) {} }

  /* ---------------- render ---------------- */
  function render() {
    var s = curStep(), qs = quizSteps(), qi = qs.indexOf(s);
    stepEl.textContent = qi >= 0 ? ("Step " + (qi + 1) + " of " + qs.length) : "";
    fillEl.style.width = qi >= 0 ? Math.round(((qi + 1) / qs.length) * 100) + "%" : (s === "start" ? "0%" : "100%");
    back.style.visibility = (i === 0 || s === "done") ? "hidden" : "visible";
    skip.style.display = (OPTIONAL[s] || s.indexOf("brand:") === 0) ? "inline" : "none";
    next.style.display = (s === "done") ? "none" : "inline-block";
    body.innerHTML = "";
    // Default CTA label for every step; steps that need another label (widthconfirm,
    // shape, contact) override it below. Without this, navigating Back from a final
    // step kept its label (e.g. "Select this size") on the intermediate steps.
    next.textContent = "Continue";
    if (s.indexOf("brand:") === 0) rBrand(s.slice(6));
    else ({ start: rStart, measure: rMeasure, widthconfirm: rWidthConfirm,
            brands: rBrands, brannock: rBrannock, problems: rProblems, shape: rShape,
            result: rResult, contact: rContact, done: rDone }[s])();
    validate();
  }

  function rStart() {
    next.textContent = "Continue";
    var intro = document.createElement("p"); intro.className = "ff-help";
    intro.textContent = "A few quick questions and we'll size you exactly, length and width.";
    body.appendChild(intro);
    var saved = loadProfile();
    if (saved && saved.us) {
      var wb = document.createElement("div"); wb.className = "ff-welcome";
      wb.innerHTML = '<div class="ff-help" style="margin:0 0 4px">Welcome back, your saved size</div><div class="sz">US ' + saved.us + ' · ' + saved.width + '</div>';
      var use = document.createElement("button"); use.className = "ff-inline"; use.style.marginTop = "8px"; use.textContent = "Use my saved size →";
      use.onclick = function () {
        var rec = { recommendedSize: { us: saved.us }, recommendedWidth: { code: saved.width } };
        applySize(rec, state.scope); writeProps(state.scope, propsFor(rec, null)); close();
      };
      wb.appendChild(use); body.appendChild(wb);
    }
    numHead("Fitting for");
    var seg = document.createElement("div"); seg.className = "ff-seg";
    [["men", "Men's"], ["women", "Women's"]].forEach(function (p) {
      var b = btnTile(p[1], state.gender === p[0]); b.onclick = function () { state.gender = p[0]; render(); }; seg.appendChild(b);
    });
    body.appendChild(seg);
    numHead("How do you want to find your size?");
    methodCard("measure", "Measure my feet", "About 5 minutes with a tape measure", "Most accurate", "📏");
    methodCard("brannock", "I know my Brannock size", "The size a shoe-store foot measurer read", "Very accurate", "📐");
    methodCard("brands", "I know my size in another brand", "Nike, Adidas, Cole Haan and more", null, "👟");
  }

  function methodCard(key, t, s, badge, icon) {
    var b = document.createElement("button"); b.className = "ff-method" + (state.method === key ? " sel" : "");
    b.innerHTML = '<div class="ff-mc-row"><span class="ff-mc-ic">' + (icon || "") + '</span>' +
      '<span class="ff-mc-tx"><span class="t">' + t + '</span><span class="s">' + s + '</span></span>' +
      (badge ? '<span class="ff-badge2">' + badge + '</span>' : '') + '</div>';
    b.onclick = function () { state.method = key; render(); };
    body.appendChild(b);
  }

  function rMeasure() {
    var u = state.measurement.unit || "cm", ph = UNIT_PH[u], img = CFG.images || {};
    var lenHelp = "Stand on paper with your heel to a wall and mark your longest toe. Measure wall to mark.";
    var girHelp = "Wrap a soft tape all the way around the ball of your foot (the widest part) and note where it meets.";
    var girLbl = "Around the ball of your foot";
    q("Measure your feet", "Measure in the evening, when feet are at their largest, and wear the socks you'll wear with the shoes.");
    if (state.measureReason) {
      var rn = document.createElement("div"); rn.className = "ff-callout";
      rn.innerHTML = state.measureReason === "conflict"
        ? "The sizes you entered pointed to different fits, so we won't guess. Measuring is the most accurate way — 99% of customers who measure get an accurate fit."
        : "We don't keep a size chart for that brand, so rather than guess we'll size you from your own measurements.";
      body.appendChild(rn);
    }
    guideCallout("Download the printable fit guide (PDF).");
    numHead("Units");
    var seg = document.createElement("div"); seg.className = "ff-seg"; seg.style.maxWidth = "220px";
    [["cm", "cm"], ["in", "in"]].forEach(function (p) {
      var b = btnTile(p[1], u === p[0]); b.onclick = function () { setUnit(p[0]); }; seg.appendChild(b);
    });
    body.appendChild(seg);
    numHead("Are your feet the same size?");
    var fseg = document.createElement("div"); fseg.className = "ff-seg";
    [["Same", "Same"], ["Different", "Different"]].forEach(function (p) {
      var b = btnTile(p[1], (state.measurement.feet_match || "Same") === p[0]);
      b.onclick = function () { state.measurement.feet_match = p[0]; render(); }; fseg.appendChild(b);
    });
    body.appendChild(fseg);
    if (feetDiffer()) {
      // Left + right side by side per measurement — one row each, not four stacked.
      mPair(img.length, "Foot length", "lenL", "lenR", ph.len, u, lenHelp);
      mPair(img.width, girLbl, "girthL", "girthR", ph.girth, u, girHelp);
    } else {
      mRow(img.length, "Foot length", "len", ph.len[0], u, lenHelp);
      mRow(img.width, girLbl, "girth", ph.girth[0], u, girHelp);
    }
    var t = document.createElement("div"); t.className = "ff-mini";
    t.textContent = feetDiffer()
      ? "Measure each foot and enter both. Tap a photo to see how."
      : "Measure both feet; if they differ, choose Different above. Tap a photo to see how.";
    body.appendChild(t);
  }
  function mRow(src, label, key, placeholder, unit, help) {
    var row = document.createElement("div"); row.className = "ff-mrow";
    var thumb = document.createElement("button"); thumb.type = "button"; thumb.className = "ff-thumb";
    thumb.innerHTML = (src ? '<img src="' + src + '" alt="">' : "") + '<span class="zi">⤢</span>';
    thumb.onclick = function () { openLightbox(src, label, help); };
    var col = document.createElement("div"); col.className = "ff-fcol";
    var l = document.createElement("label"); l.className = "ff-lbl"; l.style.margin = "0 0 6px"; l.textContent = label; col.appendChild(l);
    var wrap = document.createElement("div"); wrap.className = "ff-inwrap";
    var inp = document.createElement("input"); inp.type = "number"; inp.placeholder = placeholder; inp.value = state.measurement[key] || "";
    inp.style.paddingRight = "44px";
    inp.oninput = function (e) { state.measurement[key] = parseFloat(e.target.value) || 0; validate(); };
    var un = document.createElement("span"); un.className = "ff-unit"; un.textContent = unit;
    wrap.appendChild(inp); wrap.appendChild(un); col.appendChild(wrap);
    row.appendChild(thumb); row.appendChild(col); body.appendChild(row);
  }
  // Compact "different feet" row: one thumbnail + a Left/Right input pair for a single
  // measurement, so both feet fit on one screen instead of four stacked rows.
  function mPair(src, labelText, keyL, keyR, phArr, unit, help) {
    var row = document.createElement("div"); row.className = "ff-mrow";
    var thumb = document.createElement("button"); thumb.type = "button"; thumb.className = "ff-thumb";
    thumb.innerHTML = (src ? '<img src="' + src + '" alt="">' : "") + '<span class="zi">⤢</span>';
    thumb.onclick = function () { openLightbox(src, labelText, help); };
    var col = document.createElement("div"); col.className = "ff-fcol";
    var l = document.createElement("label"); l.className = "ff-lbl"; l.style.margin = "0 0 6px"; l.textContent = labelText; col.appendChild(l);
    var two = document.createElement("div"); two.className = "ff-2col";
    two.appendChild(footInput("Left", keyL, phArr[0], unit));
    two.appendChild(footInput("Right", keyR, phArr[1], unit));
    col.appendChild(two);
    row.appendChild(thumb); row.appendChild(col); body.appendChild(row);
  }
  function footInput(cap, key, placeholder, unit) {
    var box = document.createElement("div");
    var c = document.createElement("div"); c.className = "ff-subcap"; c.textContent = cap; box.appendChild(c);
    var wrap = document.createElement("div"); wrap.className = "ff-inwrap";
    var inp = document.createElement("input"); inp.type = "number"; inp.placeholder = placeholder; inp.value = state.measurement[key] || "";
    inp.style.paddingRight = "36px";
    inp.oninput = function (e) { state.measurement[key] = parseFloat(e.target.value) || 0; validate(); };
    var un = document.createElement("span"); un.className = "ff-unit"; un.textContent = unit;
    wrap.appendChild(inp); wrap.appendChild(un); box.appendChild(wrap);
    return box;
  }
  function openLightbox(src, label, help) {
    var lb = document.createElement("div"); lb.className = "ff-lb";
    lb.innerHTML = '<button class="ff-lb-x" aria-label="Close">×</button>' + (src ? '<img src="' + src + '" alt="' + label + '">' : "") +
      '<div class="cap"><div class="t">' + label + '</div>' + (help ? '<div class="h">' + help + '</div>' : "") + '</div>';
    lb.onclick = function () { if (lb.parentNode) lb.parentNode.removeChild(lb); };
    document.body.appendChild(lb);
  }
  function setUnit(u) {
    var old = state.measurement.unit || "cm";
    if (u === old) return;
    var fo = UNIT_MM[old], fn = UNIT_MM[u], dec = u === "mm" ? 0 : 1;
    ["len", "girth", "lenL", "girthL", "lenR", "girthR"].forEach(function (k) {
      var v = +state.measurement[k] || 0; if (v > 0) state.measurement[k] = +((v * fo) / fn).toFixed(dec);
    });
    state.measurement.unit = u; render();
  }
  function numHead(t) { var d = document.createElement("div"); d.className = "ff-numlbl"; d.textContent = t; body.appendChild(d); }

  function rWidthConfirm() {
    next.textContent = "See my recommendation";
    var prev = F.recommend(buildAnswers());
    var wname = prev.ok ? (prev.recommendedWidth.name + " (" + prev.recommendedWidth.code + ")") : "Standard";
    q("Width confirmed", "The measurement around your foot gives us a precise width.");
    var c = document.createElement("div"); c.className = "ff-check";
    c.innerHTML = '<div class="ck">✓</div><div><div class="t">Your width: <span style="color:var(--accent)">' + wname + '</span></div><div class="s">From the measurement around your foot</div></div>';
    body.appendChild(c);
    var p = document.createElement("p"); p.className = "ff-help"; p.style.marginTop = "16px"; p.textContent = "Click Continue to see your full recommendation."; body.appendChild(p);
  }

  function rBrands() {
    q("Which shoes do you wear now?", "Pick the brands you know your size in, up to 3 sneakers and 3 dress shoes. We'll ask your size for each next.");
    numHead("Your sizes are in");
    var seg = document.createElement("div"); seg.className = "ff-seg"; seg.style.maxWidth = "280px";
    ["US", "UK", "EU"].forEach(function (r) { var b = btnTile(r, state.region === r); b.onclick = function () { state.region = r; render(); }; seg.appendChild(b); });
    body.appendChild(seg);
    brandGroup("Sneakers", SNEAKER_BRANDS, state.brands.sneaker);
    brandGroup("Dress shoes", DRESS_BRANDS, state.brands.dress);
    // No guessing for brands we don't have a real size chart for — measure instead.
    var no = document.createElement("div"); no.className = "ff-nobrand";
    var msg = document.createElement("div"); msg.className = "ff-nobrand-msg";
    msg.innerHTML = "<b>Don't see your brand?</b> We won't guess. Measure your feet instead — it's the most accurate way and takes about 5 minutes.";
    no.appendChild(msg);
    var mBtn = document.createElement("button"); mBtn.type = "button"; mBtn.className = "ff-nobrand-btn";
    mBtn.textContent = "Measure my feet instead →";
    mBtn.onclick = function () { goMeasure("unknown_brand"); };
    no.appendChild(mBtn);
    body.appendChild(no);
    guideCallout("Prefer paper? Download the printable fit guide (PDF).");
  }
  function brandGroup(title, list, arr) {
    var h = document.createElement("div"); h.className = "ff-gtitle";
    h.innerHTML = title + ' <span class="n">(' + arr.length + '/' + MAX_PER_GROUP + ')</span>'; body.appendChild(h);
    var wrap = document.createElement("div"); wrap.className = "ff-chips";
    list.forEach(function (k) {
      var on = arr.indexOf(k) >= 0, atCap = !on && arr.length >= MAX_PER_GROUP;
      var b = btnTile(F.BRANDS[k].label, on, atCap);
      b.onclick = function () {
        var idx = arr.indexOf(k);
        if (idx >= 0) { arr.splice(idx, 1); delete state.entries[k]; }
        else if (arr.length < MAX_PER_GROUP) { arr.push(k); state.entries[k] = state.entries[k] || { fit: "just_right", width: "" }; }
        render();
      };
      wrap.appendChild(b);
    });
    body.appendChild(wrap);
  }

  function rBrand(key) {
    var b = F.BRANDS[key], e = state.entries[key] || (state.entries[key] = { fit: "just_right", width: "" });
    q("Your " + b.label + " size", "What size do you wear in " + b.label + "? Pick the size, then tell us how it fits.");
    label("Size (" + state.region + ")");
    body.appendChild(tileGrid(sizeValues(state.region, state.gender), "", e.size, function (v) { e.size = v; render(); }));
    label("How does your " + b.label + " pair fit you?");
    var feel = document.createElement("div"); feel.className = "ff-feel";
    [["snug", "Snug / tight"], ["just_right", "Just right"], ["roomy", "Roomy / loose"]].forEach(function (p) {
      var t = btnTile(p[1], e.fit === p[0]); t.onclick = function () { e.fit = p[0]; render(); }; feel.appendChild(t);
    });
    body.appendChild(feel);
    if (b.kind === "dress") {
      label("Do you wear a specific width in " + b.label + "?");
      var wc = document.createElement("div"); wc.className = "ff-chips";
      DRESS_WIDTHS.forEach(function (w) {
        var t = btnTile(w.label, (e.width || "") === w.v); t.onclick = function () { e.width = w.v; render(); }; wc.appendChild(t);
      });
      body.appendChild(wc);
    }
  }

  function rBrannock() {
    var e = state.brannock || (state.brannock = { size: "", width: "" });
    q("Your Brannock size", "The Brannock Device is the metal foot-measurer in shoe stores. If you've been fitted, enter what it read — the length size and the width letter. It's the same scale our sizing is built on, so there's no guesswork.");
    guideCallout("Not sure of your Brannock size? Print the fit guide and measure at home instead.");
    label("Length size (US)");
    body.appendChild(tileGrid(sizeValues("US", state.gender), "", e.size, function (v) { e.size = v; render(); }));
    label("Width letter");
    var wc = document.createElement("div"); wc.className = "ff-chips";
    BRANNOCK_WIDTHS.forEach(function (w) {
      var t = btnTile(w.label, (e.width || "") === w.v); t.onclick = function () { e.width = w.v; render(); }; wc.appendChild(t);
    });
    body.appendChild(wc);
  }

  // Route the user into the manual-measurement flow (used when we don't chart their
  // brand — we measure instead of guessing). reason drives the note shown on that step.
  function goMeasure(reason) {
    state.method = "measure"; state.measurement = { unit: "cm", feet_match: "Same" };
    state.measureReason = reason || null;
    i = stepList().indexOf("measure"); render();
  }
  // Prominent, hard-to-miss link to the printable fit guide (PDF).
  function guideCallout(text) {
    if (!CFG.guideUrl) return;
    var a = document.createElement("a");
    a.href = CFG.guideUrl; a.target = "_blank"; a.rel = "noopener"; a.className = "ff-guide";
    a.innerHTML = '<span class="ff-guide-ic">📄</span>' +
      '<span class="ff-guide-tx"><b>' + (text || "Download the printable fit guide (PDF)") + '</b>' +
      '<span>Step-by-step: measure your foot length and width at home in a few minutes.</span></span>' +
      '<span class="ff-guide-arw">↓</span>';
    body.appendChild(a);
  }

  function rProblems() {
    q("Any fit problems with your shoes today?", "Pick anything that sounds familiar. This is how we fix what off-the-shelf brands get wrong. None? Just skip.");
    var wrap = document.createElement("div"); wrap.className = "ff-problems";
    PROBLEMS.forEach(function (p) {
      var b = btnTile(p.label, state.problems.indexOf(p.k) >= 0);
      b.onclick = function () {
        if (p.k === "none") state.problems = ["none"];
        else { state.problems = state.problems.filter(function (x) { return x !== "none"; });
          var j = state.problems.indexOf(p.k); if (j >= 0) state.problems.splice(j, 1); else state.problems.push(p.k); }
        render();
      };
      wrap.appendChild(b);
    });
    body.appendChild(wrap);
  }

  function rShape() {
    q("How would you describe your foot's width?", "Skip this if you already told us a dress-shoe width. Otherwise pick what sounds most like you.");
    var wrap = document.createElement("div"); wrap.className = "ff-shapes";
    SHAPES.forEach(function (sh) {
      var b = document.createElement("button"); b.className = "ff-shape" + (state.shape.key === sh.key ? " sel" : "");
      b.innerHTML = (sh.pop ? '<span class="ff-pop">Most popular</span>' : '') + footSVG(sh.w) + '<div class="st">' + sh.t + '</div><div class="ss">' + sh.s + '</div>';
      b.onclick = function () { state.shape.key = sh.key; advance(); }; wrap.appendChild(b);
    });
    body.appendChild(wrap);
  }

  function buildAnswers() {
    var gg = state.gender === "women" ? "women" : "men";
    if (state.method === "measure") {
      var m = state.measurement, f = UNIT_MM[m.unit || "cm"];           // convert entered unit -> mm
      var toMm = function (k) { return Math.round((+m[k] || 0) * f); };
      var meas = feetDiffer()
        ? { leftLen: toMm("lenL"), rightLen: toMm("lenR"), leftGirth: toMm("girthL"), rightGirth: toMm("girthR") }
        : (function () { var l = toMm("len"), gth = toMm("girth"); return { leftLen: l, rightLen: l, leftGirth: gth, rightGirth: gth }; })();
      return { gender: gg, anchors: [], measurement: meas,
        problems: (state.problems || []).filter(function (p) { return p !== "none"; }) };
    }
    if (state.method === "brannock") {
      var be = state.brannock || {};
      var bAns = { gender: gg, anchors: [],
        problems: (state.problems || []).filter(function (p) { return p !== "none"; }) };
      // fit:null → engine trusts the Brannock length as-is (no worn-shoe feel adjustment).
      if (be.size) bAns.anchors.push({ brand: "brannock", size: F.regionToUs(be.size, gg, "US"), fit: null });
      if (be.width) bAns.width = { source: "known", letter: be.width };
      return bAns;
    }
    var a = [], dressWidth = null;
    selectedBrands().forEach(function (k) {
      var e = state.entries[k]; if (!e || !e.size) return;
      a.push({ brand: k, size: F.regionToUs(e.size, gg, state.region), fit: e.fit });
      if (F.BRANDS[k].kind === "dress" && e.width) dressWidth = e.width;
    });
    var ans = { gender: gg, anchors: a, problems: state.problems.filter(function (p) { return p !== "none"; }) };
    if (dressWidth) ans.width = { source: "known", letter: dressWidth };
    else if (state.shape.key) ans.width = { source: "known", letter: SHAPE_WIDTHS[gg][state.shape.key] };
    return ans;
  }

  // Shown when brand sizes disagree too much to size confidently. No guessed size —
  // we explain why, prove measuring works, and route to the measurement flow.
  function rConflict() {
    next.style.display = "none";
    var wrap = document.createElement("div"); wrap.className = "ff-conflict";
    wrap.innerHTML =
      '<div class="ff-q">Let\'s measure to be sure</div>' +
      '<p class="ff-help">The sizes you entered point to different fits, so we won\'t guess. Measuring your feet takes about 5 minutes and is the most accurate way to get your made-to-fit pair right.</p>' +
      '<div class="ff-proof"><span class="pct">99%</span><span class="pt">of customers who measure get an accurate fit — backed by our 4.98★ rating from 8,000+ reviews.</span></div>';
    body.appendChild(wrap);
    guideCallout("Download the printable fit guide (PDF).");
    var btn = document.createElement("button"); btn.type = "button"; btn.className = "ff-primary";
    btn.style.cssText = "display:block;width:100%;margin-top:6px;";
    btn.textContent = "Measure my feet →";
    btn.onclick = function () { goMeasure("conflict"); };
    body.appendChild(btn);
  }

  function rResult() {
    result = F.recommend(buildAnswers());
    next.textContent = CFG.captureContact ? "Save my profile" : "Select this size";
    if (!result.ok) { body.innerHTML = '<div class="ff-q">We need one shoe to start</div><p class="ff-help">Go back and add at least one brand + size you already wear, and we\'ll do the rest.</p>'; next.disabled = true; return; }
    // Brand sizes that can't be reconciled: we won't guess a size. Show why, prove that
    // measuring works, and send them to the measurement flow.
    if (result.conflict) { rConflict(); return; }
    var r = result, sz = r.recommendedSize;
    var cv = sz.eu ? ((sz.euUkApprox ? "≈ " : "") + "EU " + sz.eu + " · UK " + sz.uk) : "";
    var cm = (r.estimatedFootLengthMm / 10).toFixed(1), inch = (r.estimatedFootLengthMm / 25.4).toFixed(1);
    body.innerHTML =
      '<div class="ff-res"><div class="sz">US ' + sz.us + ' · ' + r.recommendedWidth.code + '</div>' +
        '<div class="cv">' + r.recommendedWidth.name + (cv ? ' &nbsp;·&nbsp; ' + cv : '') + '</div>' +
        '<div class="cv">Your foot ≈ ' + cm + ' cm · ' + inch + ' in</div>' +
        '<span class="ff-chip ' + r.confidence + '">' + ({ high: "We're confident", medium: "Good match", low: "Let's confirm" }[r.confidence]) + '</span></div>' +
      (r.problems && r.problems.length ? '<div class="ff-callout"><b>We\'ll build in fixes for:</b> ' + r.problems.map(problemLabel).join(", ") + '.</div>' : '') +
      '<div class="ff-rec">' + recCopy(r, cm, inch) + '</div>';
    if (state.method !== "measure") {
      var mBtn = document.createElement("button"); mBtn.className = "ff-inline";
      mBtn.style.cssText = "display:block;margin:14px auto 0;"; mBtn.textContent = "Measure to confirm instead";
      mBtn.onclick = function () { goMeasure(null); };
      body.appendChild(mBtn);
    }
  }

  // Friendly, customer-facing recommendation text (no internal offsets/mm math).
  function recCopy(r, cm, inch) {
    var sz = r.recommendedSize, w = r.recommendedWidth, n = (r.inputsUsed || []).length;
    var lead = ({
      high:   "You're a clear <b>US " + sz.us + "</b> in a <b>" + w.name + " (" + w.code + ")</b> fitting.",
      medium: "We recommend <b>US " + sz.us + "</b> in a <b>" + w.name + " (" + w.code + ")</b> fitting.",
      low:    "Your best starting point is <b>US " + sz.us + "</b> in a <b>" + w.name + " (" + w.code + ")</b> fitting."
    })[r.confidence];
    var measured = (r.inputsUsed || []).some(function (e) { return e.source === "measured"; });
    var mid = measured
      ? " Your foot measures about <b>" + cm + " cm (" + inch + " in)</b>, and that's the size that fits true on our last."
      : " Comparing the " + (n === 1 ? "pair" : n + " pairs") + " you already wear, your foot works out to about <b>" +
        cm + " cm (" + inch + " in)</b>. That's the size that fits true on our last.";
    var extra = " Every pair is made to your measurements, so we'll build this to your exact foot.";
    if (r.confidence === "low") extra += " Add another brand you wear, or measure your feet, and we'll make it even more precise.";
    return lead + mid + extra;
  }

  function rContact() {
    next.textContent = "Save my profile";
    var c = state.contact;
    body.innerHTML =
      '<div class="ff-q">Save your fit profile</div>' +
      '<p class="ff-help">Add your email and phone and we\'ll save your size + width to your profile so it\'s <b>applied automatically every time you shop</b>, and we can pick styles that fit without you hunting through sizes.</p>' +
      '<label class="ff-lbl">Email</label><input type="email" data-c="email" placeholder="you@email.com" value="' + (c.email || "") + '"><div class="ff-err" data-e="email">Please enter a valid email.</div>' +
      '<label class="ff-lbl">Phone</label><input type="tel" data-c="phone" placeholder="+1 555 000 0000" value="' + (c.phone || "") + '"><div class="ff-err" data-e="phone">Please enter a valid phone number.</div>' +
      '<div class="ff-mini">For fit updates and order help by text.</div>' +
      '<label class="ff-consent"><input type="checkbox" data-c="consent" ' + (c.consent ? "checked" : "") + ' style="width:auto"> Save my profile and send me my fit + occasional updates. I can opt out anytime.</label>';
    body.querySelector('[data-c="email"]').oninput = function (e) { state.contact.email = e.target.value.trim(); validate(); };
    body.querySelector('[data-c="phone"]').oninput = function (e) { state.contact.phone = e.target.value.trim(); validate(); };
    body.querySelector('[data-c="consent"]').onchange = function (e) { state.contact.consent = e.target.checked; };
    if (CFG.turnstileSiteKey) {
      var tsBox = document.createElement("div"); tsBox.className = "ff-ts"; body.appendChild(tsBox);
      state.contact.turnstileToken = "";
      loadTurnstile(function () {
        if (!document.body.contains(tsBox) || !window.turnstile) return;
        try {
          window.turnstile.render(tsBox, {
            sitekey: CFG.turnstileSiteKey,
            callback: function (tok) { state.contact.turnstileToken = tok; validate(); },
            "expired-callback": function () { state.contact.turnstileToken = ""; validate(); },
            "error-callback": function () { state.contact.turnstileToken = ""; validate(); }
          });
        } catch (e) { /* already rendered */ }
      });
    }
  }
  // Load Cloudflare Turnstile once, then run cb (immediately if already present).
  var _tsQueue = null;
  function loadTurnstile(cb) {
    if (window.turnstile) { cb(); return; }
    if (_tsQueue) { _tsQueue.push(cb); return; }
    _tsQueue = [cb];
    window.__ffTsReady = function () { var q = _tsQueue || []; _tsQueue = null; q.forEach(function (f) { f(); }); };
    var s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__ffTsReady&render=explicit";
    s.async = true; s.defer = true; document.head.appendChild(s);
  }

  function rDone() {
    var r = result, sz = r.recommendedSize;
    body.innerHTML =
      '<div class="ff-done"><div class="big">✓</div><div class="ff-q" style="margin-top:6px">Profile saved and applied</div>' +
      '<p class="ff-help">Your fit: <b>US ' + sz.us + ' · ' + r.recommendedWidth.code + '</b> (' + r.recommendedWidth.name + '). We\'ll apply it as you shop and email a copy to ' + state.contact.email + '.</p>' +
      '<button class="ff-primary" data-apply="1">Select this size &amp; continue</button></div>';
    body.querySelector("[data-apply]").onclick = function () {
      var applied = applySize(r, state.scope);
      writeProps(state.scope, propsFor(r, state));
      document.dispatchEvent(new CustomEvent("fitfinder:selected", { detail: { result: r, contact: state.contact, applied: applied } }));
      close();
    };
  }

  /* ---------------- nav & validation ---------------- */
  function validate() {
    var s = curStep(), ok = true;
    if (s === "start") ok = !!state.gender && !!state.method;
    if (s === "measure") ok = measureValid();
    if (s === "brands") ok = selectedBrands().length >= 1;
    if (s === "brannock") ok = !!(state.brannock && state.brannock.size);
    if (s.indexOf("brand:") === 0) { var e = state.entries[s.slice(6)]; ok = !!(e && e.size); }
    if (s === "result") ok = result && result.ok;
    if (s === "contact") ok = validEmail(state.contact.email) && validPhone(state.contact.phone) && (!CFG.turnstileSiteKey || !!state.contact.turnstileToken);
    next.disabled = !ok;
  }
  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || ""); }
  function validPhone(v) { return (v || "").replace(/[^\d]/g, "").length >= 7; }

  function goBack() { if (i > 0) { i--; render(); } }
  function advance() { if (i < stepList().length - 1) { i++; render(); } }
  function onSkip() {
    var s = curStep();
    if (s.indexOf("brand:") === 0) {         // skipping a brand removes it, list shrinks under us
      var k = s.slice(6);
      ["sneaker", "dress"].forEach(function (grp) { var j = state.brands[grp].indexOf(k); if (j >= 0) state.brands[grp].splice(j, 1); });
      delete state.entries[k]; render();      // same index now points at the next step
    } else advance();
  }
  function goNext() {
    var s = curStep();
    if (s === "contact") { submitLead(); return; }
    if (s === "result" && !CFG.captureContact) { finishLocal(); return; }  // no backend: save locally + apply
    advance();
  }
  function finishLocal() {
    saveProfile({ us: result.recommendedSize.us, width: result.recommendedWidth.code,
                  gender: state.gender, region: state.region, ts: new Date().toISOString() });
    var applied = applySize(result, state.scope);
    writeProps(state.scope, propsFor(result, state));
    document.dispatchEvent(new CustomEvent("fitfinder:selected", { detail: { result: result, applied: applied } }));
    close();
  }

  function submitLead() {
    if (!validEmail(state.contact.email)) { body.querySelector('[data-e="email"]').classList.add("show"); return; }
    if (!validPhone(state.contact.phone)) { body.querySelector('[data-e="phone"]').classList.add("show"); return; }
    saveProfile({ us: result.recommendedSize.us, width: result.recommendedWidth.code, gender: state.gender, region: state.region, email: state.contact.email, ts: new Date().toISOString() });
    var payload = { contact: state.contact, answers: state, recommendation: result, turnstileToken: state.contact.turnstileToken || null, ts: new Date().toISOString() };
    if (CFG.submitEndpoint) {
      next.disabled = true; next.textContent = "Saving…";
      fetch(CFG.submitEndpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }).then(toDone, toDone);
    } else { console.log("[FitFinder] profile payload (set FIT_FINDER.submitEndpoint to send):", payload); toDone(); }
  }
  function toDone() { i = stepList().indexOf("done"); render(); }

  /* ---------------- capture submitted fit details as cart line-item properties ------ */
  // Full internal record (stashed in a hidden `_fit_data` property for manufacturing).
  function fitData(r, st) {
    var d = { v: 3, method: st.method, gender: st.gender, region: st.region,
      rec: { us: r.recommendedSize && r.recommendedSize.us, width: (r.recommendedWidth || {}).code,
             confidence: r.confidence, footLenMm: r.estimatedFootLengthMm } };
    if (CFG.itemType) d.item_type = CFG.itemType;              // 'shoes' | 'boots' (from the PDP metafield)
    if (st.method === "measure") {
      var m = st.measurement, f = UNIT_MM[m.unit || "cm"], differ = m.feet_match === "Different";
      var mm = function (k) { return Math.round((+m[k] || 0) * f); };
      d.feet_match = differ ? "Different" : "Same";
      d.unit = m.unit || "cm";
      d.measurement = differ
        ? { length_left_mm: mm("lenL"), length_right_mm: mm("lenR"), girth_left_mm: mm("girthL"), girth_right_mm: mm("girthR") }
        : { length_mm: mm("len"), girth_mm: mm("girth") };
    } else { d.brands = st.entries; }
    d.problems = (st.problems || []).filter(function (x) { return x && x !== "none"; });
    if (st.shape && st.shape.key) d.shape = st.shape.key;
    return d;
  }
  // Customer/order-facing properties (+ hidden `_fit_data`). `st` is null for saved-size reuse.
  function propsFor(r, st) {
    var p = {}, us = r.recommendedSize && r.recommendedSize.us, w = r.recommendedWidth || {};
    if (st) p["Fit method"] = st.method === "measure" ? "Measured feet" : "Matched to brands worn";
    if (us != null) p["Fit size (US)"] = "US " + us;
    if (w.code) p["Fit width"] = w.code + (w.name ? " (" + w.name + ")" : "");
    if (st && st.method === "measure") {
      var m = st.measurement, f = UNIT_MM[m.unit || "cm"], differ = m.feet_match === "Different";
      var cm = function (k) { return ((+m[k] || 0) * f / 10).toFixed(1); };
      var inch = function (k) { return ((+m[k] || 0) * f / 25.4).toFixed(1); };
      if (differ) {
        p["Feet"] = "Different (left & right captured)";
        p["Foot length"] = "L " + cm("lenL") + " cm · R " + cm("lenR") + " cm";
        p["Ball girth"] = "L " + cm("girthL") + " cm · R " + cm("girthR") + " cm";
      } else {
        p["Foot length"] = cm("len") + " cm / " + inch("len") + " in";
        p["Ball girth"] = cm("girth") + " cm";
      }
    } else if (r.estimatedFootLengthMm) {                       // brands path — show the estimate
      p["Foot length"] = (r.estimatedFootLengthMm / 10).toFixed(1) + " cm / " + (r.estimatedFootLengthMm / 25.4).toFixed(1) + " in";
    }
    if (st) {
      var probs = (st.problems || []).filter(function (x) { return x && x !== "none"; });
      if (probs.length) p["Fit notes"] = probs.map(problemLabel).join(", ");
      p["_fit_data"] = JSON.stringify(fitData(r, st));
    }
    return p;
  }
  // Attach the properties to the product form so they submit with add-to-cart.
  // Prefers the `form=` attribute (this theme's fields live outside the <form>).
  function writeProps(scope, props) {
    document.querySelectorAll("input[data-ff-prop]").forEach(function (n) { n.parentNode && n.parentNode.removeChild(n); });
    var fid = CFG.productFormId, useAttr = !!(fid && document.getElementById(fid)), container;
    if (useAttr) { container = document.body; }
    else {
      container = (scope && scope.tagName === "FORM") ? scope
        : (scope && scope.querySelector && scope.querySelector("form[action*='/cart/add']"))
        || document.querySelector("form[action*='/cart/add']");
      if (!container) return false;
    }
    Object.keys(props).forEach(function (k) {
      var val = props[k]; if (val == null || val === "") return;
      var inp = document.createElement("input"); inp.type = "hidden";
      inp.name = "properties[" + k + "]"; inp.value = String(val); inp.setAttribute("data-ff-prop", "1");
      if (useAttr) inp.setAttribute("form", fid);
      container.appendChild(inp);
    });
    return true;
  }

  /* ---------------- apply recommended size/width to the product form ---------------- */
  function normWidth(s) { s = (s || "").toString().trim().toUpperCase().replace(/\s+/g, ""); var m = s.match(/^(\d)E$/); return m ? new Array(+m[1] + 1).join("E") : s; }
  function activate(el) { if (el.tagName === "INPUT") { if (!el.checked) el.click(); el.dispatchEvent(new Event("change", { bubbles: true })); } else el.click(); }
  function applySize(rec, scope) {
    var us = rec.recommendedSize.us, wcode = rec.recommendedWidth.code, done = { size: false, width: false }, focusEl = null;
    function matchNum(v) { return parseFloat(String(v).replace(/[^0-9.]/g, "")); }
    function run(root) {
      root.querySelectorAll("input.radio__input, input[type=radio][data-single-option-selector]").forEach(function (r) {
        var name = (r.getAttribute("name") || "").toLowerCase(), v = r.value || "";
        if (name.indexOf("width") >= 0) { if (!done.width && normWidth(v.split(" - ")[0]) === normWidth(wcode)) { activate(r); done.width = true; } }
        else if (name.indexOf("size") >= 0) { if (!done.size && matchNum(v) === parseFloat(us)) { activate(r); done.size = true; focusEl = r; } }
      });
      root.querySelectorAll(".select__fieldset, popout-select").forEach(function (fs) {
        var lbl = ((fs.querySelector(".select__label") || {}).textContent || "").toLowerCase();
        var isW = lbl.indexOf("width") >= 0, isS = lbl.indexOf("size") >= 0; if (!isW && !isS) return;
        fs.querySelectorAll("[data-popout-option]").forEach(function (a) {
          var v = a.getAttribute("data-value") || a.textContent || "";
          if (isW) { if (!done.width && normWidth(v.split(" - ")[0]) === normWidth(wcode)) { a.click(); done.width = true; } }
          else if (isS) { if (!done.size && matchNum(v) === parseFloat(us)) { a.click(); done.size = true; focusEl = a; } }
        });
      });
    }
    run(scope || document);
    if (!done.size) run(document);
    if (focusEl && focusEl.scrollIntoView) { try { focusEl.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) {} }
    return done;
  }

  document.addEventListener("click", function (e) {
    var t = e.target.closest && e.target.closest("[data-fit-finder-open]");
    if (t) { e.preventDefault(); open({ gender: t.getAttribute("data-gender") || null, scope: t.closest("form[action*='/cart/add']") || t.closest("form") || null }); }
  });
  window.FitFinder = { open: open, close: close, applySaved: function (scope) { var p = loadProfile(); if (p && p.us) return applySize({ recommendedSize: { us: p.us }, recommendedWidth: { code: p.width } }, scope || null); } };
})();
